// Pure helpers for "Scan & advise" on a sandbox host
// (docs/plans/2026-10-03-sandbox-hosts-ontwerp.md §16.6): byte and disk texts,
// the advice headline and when a polled host shows a new scan. No React here,
// so it's unit tested on its own (sandbox-host-scan.test.ts).

import { adviseSandboxHostSlots } from '@goblic/shared'
import type { SandboxHost, SandboxHostAdvice, SandboxHostScan } from '@goblic/shared'

/** Poll the host this often while a scan is queued. */
export const SCAN_POLL_MS = 2_000
/** Give up after this: the scan queue expires after 300 s (SANDBOX_HOST_SCAN_QUEUE_OPTIONS). */
export const SCAN_POLL_LIMIT_MS = 300_000

const UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB'] as const

/** `512 MiB`, `15.6 GiB`, `1.2 TiB` (binary units, one decimal from GiB up); `–` for null. */
export function bytesText(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '–'
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit += 1
  }
  const digits = unit >= 3 && value < 100 ? 1 : 0
  return `${value.toFixed(digits).replace(/\.0$/, '')} ${UNITS[unit]}`
}

/** `120 GiB free of 250 GiB (52% used)`, or null when the scan has no disk figures. */
export function diskText(scan: SandboxHostScan | null | undefined): string | null {
  const disk = scan?.disk
  if (!disk || disk.totalBytes <= 0) return null
  const used = Math.round(((disk.totalBytes - disk.freeBytes) / disk.totalBytes) * 100)
  return `${bytesText(disk.freeBytes)} free of ${bytesText(disk.totalBytes)} (${used}% used)`
}

/** `3 heavy / 6 light`. */
export function adviceHeadline(advice: Pick<SandboxHostAdvice, 'heavySlots' | 'lightSlots'>): string {
  return `${advice.heavySlots} heavy / ${advice.lightSlots} light`
}

/**
 * The advice with its "slots are set to …" warnings measured against `slots`
 * (what the form holds now, e.g. after "Apply advice") instead of the saved
 * row the API compared with. Without `slots`, the API's advice as-is.
 */
export function adviceForSlots(
  host: Pick<SandboxHost, 'kind' | 'lastScan' | 'advice'>,
  slots?: { heavySlots: number | null; lightSlots: number | null }
): SandboxHostAdvice | null {
  if (!slots || !host.lastScan) return host.advice
  return adviseSandboxHostSlots(host.lastScan, host.kind, slots)
}

/** True once the worker stored a scan newer than the one seen when "Scan & advise" was pressed. */
export function scanFinished(before: string | null, host: Pick<SandboxHost, 'lastScanAt'>): boolean {
  return host.lastScanAt != null && host.lastScanAt !== before
}

/**
 * `3 other containers: limits of 2 CPU and 4 GiB`, plus what was not counted;
 * a zero sum is left out, and `15 other containers, none with limits` when both are.
 */
export function foreignText(foreign: NonNullable<SandboxHostScan['foreign']>): string {
  const count = `${foreign.containers} other container${foreign.containers === 1 ? '' : 's'}`
  if (foreign.containers === 0) return 'No other containers'
  const cpu = Math.round(foreign.cpuLimit * 10) / 10
  const sums = [
    cpu > 0 ? `${cpu} CPU` : null,
    foreign.memoryLimitBytes > 0 ? bytesText(foreign.memoryLimitBytes) : null,
  ].filter(Boolean)
  const partial =
    foreign.inspected < foreign.containers ? ` (${foreign.inspected} looked at)` : ''
  if (sums.length === 0) return `${count}, none with limits${partial}`
  const unlimited =
    foreign.unlimitedMemory > 0 ? `, ${foreign.unlimitedMemory} without a memory limit` : ''
  return `${count}: limits of ${sums.join(' and ')}${unlimited}${partial}`
}

/** `1 heavy · 2 light · 1 unknown`, or `None`. */
export function ownText(own: NonNullable<SandboxHostScan['own']>): string {
  const parts = [
    own.heavy > 0 ? `${own.heavy} heavy` : null,
    own.light > 0 ? `${own.light} light` : null,
    own.unknown > 0 ? `${own.unknown} unknown` : null,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : 'None'
}
